import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { PartnerDriverSearchForm } from 'src/components/partner-driver/search/PartnerDriverSearchForm'
import type { PartnerDriverSearchFormValues } from 'src/components/partner-driver/search/PartnerDriverSearchForm.interface'
import { PartnerDriversTable } from 'src/components/partner-driver/search/PartnerDriversTable'
import { AdminSearchPartnerDrivers } from 'src/constants/actionTypes'
import { buildPartnerDriverPath } from 'src/constants/routes'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { useAuthSession } from 'src/hooks/useAuthSession'
import type { PartnerDriverSearchRow } from 'src/interfaces/partnerDriver'
import {
  buildSearchParams,
  hasSearchQuery,
  readSearchQuery,
} from 'src/utils/partnerDriver/searchQuery'

/**
 * Find a driver. The query lives in the URL so the driver page's Back link (and the browser's)
 * returns to the same results; a URL change is what triggers the search.
 */
const PartnerDriverSearchPageComponent = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthSession()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()

  // Bumped by Find and Refresh, so re-submitting an unchanged query still re-runs it.
  const [refreshToken, setRefreshToken] = useState<number>(0)

  const query = useMemo(() => {
    return readSearchQuery(searchParams)
  }, [searchParams])

  const isQueryPresent = hasSearchQuery(query)

  const initialValues = useMemo<PartnerDriverSearchFormValues>(() => {
    return {
      email: searchParams.get('email') ?? '',
      phone: searchParams.get('phone') ?? '',
      partnerDriverId: searchParams.get('partnerDriverId') ?? '',
    }
  }, [searchParams])

  useEffect(() => {
    if (!isAuthenticated || !hasSearchQuery(query)) {
      return
    }

    asyncDispatchWithErrorToast(AdminSearchPartnerDrivers.request(query))
  }, [asyncDispatchWithErrorToast, isAuthenticated, query, refreshToken])

  const bumpRefreshToken = useCallback(() => {
    setRefreshToken((previousToken) => {
      return previousToken + 1
    })
  }, [])

  const handleSubmit = useCallback(
    (values: PartnerDriverSearchFormValues) => {
      setSearchParams(buildSearchParams(values))
      bumpRefreshToken()
    },
    [setSearchParams, bumpRefreshToken],
  )

  const handleOpenDriver = useCallback(
    (row: PartnerDriverSearchRow) => {
      navigate(buildPartnerDriverPath(row.id), { state: { search: location.search } })
    },
    [navigate, location.search],
  )

  return (
    <div
      data-testid="partner-driver-search-page"
      css={tw`flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden p-6`}
    >
      <Typography variant="h2" css={tw`mb-6`}>
        Update Partner Driver
      </Typography>
      <PartnerDriverSearchForm
        initialValues={initialValues}
        canRefresh={isQueryPresent}
        onSubmit={handleSubmit}
        onRefresh={bumpRefreshToken}
      />
      <PartnerDriversTable hasSearchQuery={isQueryPresent} onOpenDriver={handleOpenDriver} />
    </div>
  )
}

export const PartnerDriverSearchPage = memo(PartnerDriverSearchPageComponent)
